import styled, { css } from 'styled-components';

import {
    designColor,
    floorGameDuration as duration,
    floorGameEasing as easing,
    InputRange,
    styledInteraction,
    styledLayout,
} from '@pkg/primitives';

import { type FieldRangeSize } from './FieldRange.types';

import { tokens } from './FieldRange.tokens';

interface StyledFieldRangeProps {
    $disabled?: boolean;
    $error?: boolean;
    $progress?: number;
    $size?: FieldRangeSize;
}

interface ChildProps {
    $disabled?: boolean;
}

const inputThumbBase = css`
    width: var(--field-range-thumb-size);
    height: var(--field-range-thumb-size);
    border-radius: 999px;
`;

export const StyledFieldRange = styled.div<StyledFieldRangeProps>`
    --field-range-track-height: ${tokens.track.height.slim}px;
    --field-range-progress-height: ${tokens.progress.height.slim}px;

    --field-range-thumb-size: ${tokens.thumb.size.slim}px;
    --field-range-thumb-webkit-offset-y: calc(
        (var(--field-range-thumb-size) - var(--field-range-track-height)) / 2 * -1
    );

    --field-range-handles-gap: ${tokens.handles.gap.slim}px;
    --field-range-handles-width: ${tokens.handles.width.slim}px;
    --field-range-handles-height: ${tokens.handles.height.slim}px;

    --field-range-color-primary: ${designColor.brandPurple};
    --field-range-color-primary-light: ${designColor.brandPurpleLighter};

    --slider-progress: ${({ $progress = 0 }) => $progress};
    --slider-percent: calc(var(--slider-progress) * 1%);
    --slider-offset: calc(var(--field-range-thumb-size) * (0.5 - var(--slider-progress) / 100));

    display: grid;
    align-items: center;
    flex: 1 1 auto;
    height: var(--field-range-track-height);
    transition: opacity ${duration.normal}ms ${easing.cubic};

    ${({ $disabled }) =>
        $disabled &&
        css`
            opacity: 0.8;
        `};

    ${({ $error }) =>
        $error &&
        css`
            --field-range-color-primary: ${designColor.red};
            --field-range-color-primary-light: ${designColor.redLight};
        `};

    ${({ $size }) =>
        $size === 'thick' &&
        css`
            --field-range-track-height: ${tokens.track.height.thick}px;
            --field-range-progress-height: ${tokens.progress.height.thick}px;
            --field-range-thumb-size: ${tokens.thumb.size.thick}px;
            --field-range-handles-gap: ${tokens.handles.gap.thick}px;
            --field-range-handles-width: ${tokens.handles.width.thick}px;
            --field-range-handles-height: ${tokens.handles.height.thick}px;
        `};
`;

export const FakeTrack = styled.div<ChildProps>`
    ${styledInteraction.interactionDisable};

    display: grid;
    grid-area: 1 / 1;
    align-content: center;
    height: var(--field-range-progress-height);
    background-color: rgb(255, 255, 255, 0.2);
    border-radius: var(--field-range-progress-height);
    transition: background-color ${duration.normal}ms ${easing.cubic};

    ${StyledFieldRange}:hover &,
    ${StyledFieldRange}:focus & {
        background-color: rgb(255, 255, 255, 0.3);
    }

    ${StyledFieldRange}:active & {
        background-color: rgb(255, 255, 255, 0.26);
    }

    ${({ $disabled }) =>
        $disabled &&
        css`
            background-color: rgb(255, 255, 255, 0.2) !important;
        `};
`;

export const Progress = styled.div`
    display: grid;
    grid-area: 1 / 1;
    align-content: center;
    justify-content: end;
    width: calc(var(--slider-percent) + var(--slider-offset));
    height: var(--field-range-progress-height);
    background-color: var(--field-range-color-primary);
    border-radius: var(--field-range-progress-height);
    transition: background-color ${duration.normal}ms ${easing.cubic};
`;

export const FakeThumb = styled.div<ChildProps>`
    ${inputThumbBase};
    position: relative;
    display: grid;
    grid-auto-flow: column;
    align-content: center;
    justify-content: center;
    gap: var(--field-range-handles-gap);
    margin-right: calc(var(--field-range-thumb-size) / 2 * -1);
    background-color: white;
    transition: background-color ${duration.normal}ms ${easing.cubic};

    ${StyledFieldRange}:active & {
        background-color: var(--field-range-color-primary-light);
    }

    // Handles
    &::before,
    &::after {
        content: '';
        display: block;
        width: var(--field-range-handles-width);
        height: var(--field-range-handles-height);
        background-color: rgb(0, 0, 0, 0.6);
    }

    ${({ $disabled }) =>
        $disabled &&
        css`
            background-color: white !important;
        `};
`;

export const Output = styled.output`
    ${styledLayout.visuallyHidden};
`;

///
/// Native HTML Input

const inputTrack = css`
    height: var(--field-range-track-height);
`;

const inputThumb = css`
    ${inputThumbBase};
    color: rgb(0, 0, 0, 0.1);
    background-color: transparent;
    outline: 2px solid transparent;
    outline-offset: 2px;
    box-shadow: 0 0 0 2px currentColor;
    transition-property: color, outline-color;
    transition-duration: ${duration.normal}ms;
    transition-timing-function: ${easing.cubic};

    &:hover {
        color: rgb(255, 255, 255, 0.2);
    }
`;

const inputThumbFocus = css`
    outline-color: var(--field-range-color-primary);
`;

export const PrimitiveRange = styled(InputRange)`
    grid-area: 1 / 1;
    height: var(--field-range-track-height);

    /* Webkit */

    &::-webkit-slider-runnable-track {
        ${inputTrack};
    }

    &::-webkit-slider-thumb {
        ${inputThumb};
        /* Unlike Mozilla, we need to manually center the <thumb /> */
        margin-top: var(--field-range-thumb-webkit-offset-y);
    }

    &:focus::-webkit-slider-thumb {
        ${inputThumbFocus};
    }

    /* Mozilla */

    &::-moz-range-track {
        ${inputTrack};
    }

    &::-moz-range-thumb {
        ${inputThumb};
    }

    &:focus::-moz-range-thumb {
        ${inputThumbFocus};
    }
`;
